import incidents from '@/api/incidents';
import lookup from '@/api/lookup';
import {
  SET_INCIDENT_FIELDS,
  SET_INCIDENT,
  SET_REQUEST_CODE,
  SET_TRANSMISSION_HISTORY,
  SET_EMT_LIST_CACHE,
} from '@/store/IncidentView/mutations';

export const LOAD_INCIDENT_FIELDS = 'LOAD_INCIDENT_FIELDS';
export const LOAD_INCIDENT = 'LOAD_INCIDENT';
export const LOAD_TRANSMISSION_HISTORY = 'LOAD_TRANSMISSION_HISTORY';
export const RELOAD_FLAG_INFO = 'RELOAD_FLAG_INFO';
export const RELOAD_INCIDENT_AFTER_SAVE = 'RELOAD_INCIDENT_AFTER_SAVE';
export const FETCH_EMT_LIST = 'FETCH_EMT_LIST';

export default {
  /**
   * Load incidents list.
   *
   * @param {Function} commit - State commit.
   * @param {number} incidentId - Incident identifier.
   *
   * @return {Promise}
   */
  [LOAD_INCIDENT_FIELDS]: async ({ commit }, incidentId) => {
    const results = await incidents.fetchIncidentFields(incidentId);
    commit(SET_INCIDENT_FIELDS, results);
  },

  /**
   * Load incidents list.
   *
   * @param {Function} commit - State commit.
   * @param {number} incidentId - Incident identifier.
   *
   * @return {Promise}
   */
  [LOAD_INCIDENT]: async ({ commit }, incidentId) => {
    const params = {
      include: 'data,flag,incidentAudits,bloodTransfusionSignature',
    };
    const results = await incidents.fetchIncident(incidentId, params);

    if (typeof results === 'number' && results !== 200) {
      commit(SET_REQUEST_CODE, results);
    } else {
      commit(SET_INCIDENT, results);
    }
  },

  /**
   * Load incidents list.
   *
   * @param {Function} commit - State commit.
   * @param {number} incidentId - Incident identifier.
   *
   * @return {Promise}
   */
  [LOAD_TRANSMISSION_HISTORY]: async ({ commit }, incidentId) => {
    const results = await incidents.fetchTransmissionHistory(incidentId);
    commit(SET_TRANSMISSION_HISTORY, results);
  },

  /**
   * Reload incident flag info
   *
   * @param {Function} commit - State commit.
   * @param {Object} state - State.
   * @param {number} incidentId - Type of incidents to search
   *
   * @return {Promise}
   */
  [RELOAD_FLAG_INFO]: async ({ commit, state }, incidentId) => {
    const params = {
      include: 'flag',
    };
    const { incident } = state;
    const results = await incidents.fetchIncident(incidentId, params);
    incident.flag = results.flag;
    commit(SET_INCIDENT, incident);
  },

  /**
   * Reload incident audits
   *
   * @param {Function} commit - State commit.
   * @param {Object} state - State.
   * @param {number} incidentId - Type of incidents to search
   *
   * @return {Promise}
   */
  [RELOAD_INCIDENT_AFTER_SAVE]: async ({ commit, state }, incidentId) => {
    const params = {
      include: 'incidentAudits,bloodTransfusionSignature',
    };
    const { incident } = state;
    const results = await incidents.fetchIncident(incidentId, params);
    incident.incidentAudits = results.incidentAudits;
    incident.bloodTransfusionSigned = results.bloodTransfusionSigned;
    incident.hasBloodTransfusion = results.hasBloodTransfusion;
    incident.bloodTransfusionSignature = results.bloodTransfusionSignature;
    commit(SET_INCIDENT, incident);
  },

  /**
   * Fetch EMT list with caching
   *
   * @param {Function} commit - State commit.
   * @param {Object} state - State.
   * @param {Object} payload - Payload containing clientId and search
   *
   * @return {Promise}
   */
  [FETCH_EMT_LIST]: async ({ commit, state }, clientId) => {
    // Unique key for caching based on clientId
    const cacheKey = `client_${clientId}`;
    // Retrieve cached data if available
    const cached = state.emtListCache[cacheKey];

    // Check if we have cached data that's less than 10 minutes
    if (cached && (Date.now() - cached.timestamp) < 3000000) {
      return cached.data;
    }

    // Fetch fresh data
    const dataObj = await lookup.getEmtList(clientId, ''); // Get full data

    const items = Object.entries(dataObj).map(([value, text]) => ({
      value,
      text: `${value} - ${text}`,
    }));

    // Cache the result
    commit(SET_EMT_LIST_CACHE, { clientId, data: items });

    return items;
  },
};
